import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Cropper from "react-easy-crop";

// Every logo is stored at the same fixed size, so it looks crisp and
// consistent everywhere it's shown (admin list, member badge, downline search).
const OUTPUT_SIZE = 512;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.src = src;
  });
}

async function cropToSquareBlob(imageSrc, cropPixels) {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    OUTPUT_SIZE,
    OUTPUT_SIZE,
  );
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
}

/**
 * A drag-to-position, zoom-to-crop modal that always exports a fixed
 * OUTPUT_SIZE square JPEG, regardless of the source image's shape. Used
 * anywhere an admin uploads a logo/photo that must look uniform in a grid.
 */
export default function ImageCropModal({ show, file, title = "Crop image", onCancel, onDone }) {
  const [imageSrc, setImageSrc] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropPixels, setCropPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!file) {
      setImageSrc("");
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCropPixels(null);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const confirm = async () => {
    if (!imageSrc || !cropPixels) return;
    setBusy(true);
    try {
      const blob = await cropToSquareBlob(imageSrc, cropPixels);
      onDone(blob);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={onCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ position: "relative", width: "100%", height: 320, background: "#222" }}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_area, pixels) => setCropPixels(pixels)}
            />
          )}
        </div>
        <Form.Group className="mt-3">
          <Form.Label className="small text-muted">Zoom</Form.Label>
          <Form.Range
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="success" onClick={confirm} disabled={busy || !cropPixels}>
          {busy ? "Saving…" : "Use this crop"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
